import { useEffect, useRef, type ChangeEvent } from "react";

import type { TemperatureSeries } from "../../types";
import { seriesKey } from "../../lib/chartUtils";
import type { AnalysisFilters } from "./types";

type AnalysisTableProps = {
  series: TemperatureSeries[];
  filters: AnalysisFilters;
  onFiltersChange: (filters: AnalysisFilters) => void;
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  allFilteredSelected: boolean;
  someFilteredSelected: boolean;
  onToggleAllFiltered: (checked: boolean) => void;
  hasCity?: boolean | null;
  hasBoxes?: boolean;
  pageIndex: number;
  pageSize: number;
  filteredCount: number;
  totalCount: number;
  isLoading?: boolean;
  onPageChange: (pageIndex: number) => void;
};

export function AnalysisTable({
  series,
  filters,
  onFiltersChange,
  selectedIds,
  onToggle,
  allFilteredSelected,
  someFilteredSelected,
  onToggleAllFiltered,
  hasCity = null,
  hasBoxes = false,
  pageIndex,
  pageSize,
  filteredCount,
  totalCount,
  isLoading = false,
  onPageChange,
}: AnalysisTableProps) {
  const showCityColumn = hasCity !== false;
  const showBoxesColumn = hasBoxes;
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const firstVisible = filteredCount === 0 ? 0 : pageIndex * pageSize + 1;
  const lastVisible = Math.min(filteredCount, pageIndex * pageSize + series.length);
  const canGoPrevious = pageIndex > 0 && !isLoading;
  const canGoNext = lastVisible < filteredCount && !isLoading;

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someFilteredSelected;
  }, [someFilteredSelected]);

  const handleFilterChange = (field: keyof AnalysisFilters) => (event: ChangeEvent<HTMLInputElement>) => {
    onFiltersChange({ ...filters, [field]: event.target.value });
  };

  return (
    <div className="analysis-table chart-panel">
      <div className="analysis-table-header">
        <div>
          <h3>Available Sensors</h3>
          <p className="analysis-table-counts">
            Total: {totalCount} · Filtrés: {filteredCount} · Visibles: {firstVisible}-{lastVisible}
          </p>
        </div>
        <div className="analysis-table-pagination">
          <button type="button" onClick={() => onPageChange(pageIndex - 1)} disabled={!canGoPrevious}>
            Précédent
          </button>
          <span>Page {pageIndex + 1}</span>
          <button type="button" onClick={() => onPageChange(pageIndex + 1)} disabled={!canGoNext}>
            Suivant
          </button>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th></th>
            <th>Consignment</th>
            <th>Device</th>
            <th>Position</th>
            {showCityColumn ? <th>Destination</th> : null}
            <th>Country</th>
            {showBoxesColumn ? <th>Boxes</th> : null}
          </tr>
          <tr>
            <th>
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allFilteredSelected}
                onChange={(event) => onToggleAllFiltered(event.target.checked)}
                disabled={!series.length}
                aria-label="Select all filtered rows"
              />
            </th>
            <th>
              <input type="text" value={filters.shipmentId} onChange={handleFilterChange("shipmentId")} placeholder="Filter..." />
            </th>
            <th>
              <input type="text" value={filters.deviceId} onChange={handleFilterChange("deviceId")} placeholder="Filter..." />
            </th>
            <th>
              <input type="text" value={filters.position} onChange={handleFilterChange("position")} placeholder="Filter..." />
            </th>
            {showCityColumn ? (
              <th>
                <input
                  type="text"
                  value={filters.destinationCity}
                  onChange={handleFilterChange("destinationCity")}
                  placeholder="Filter..."
                />
              </th>
            ) : null}
            <th>
              <input type="text" value={filters.destinationCountry} onChange={handleFilterChange("destinationCountry")} placeholder="Filter..." />
            </th>
            {showBoxesColumn ? (
              <th>
                <input type="text" value={filters.boxIdentifier} onChange={handleFilterChange("boxIdentifier")} placeholder="Filter..." />
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {series.map((serie) => {
            const key = seriesKey(serie);
            const checked = selectedIds.has(key);
            return (
              <tr key={key} className={checked ? "highlight-row" : undefined}>
                <td>
                  <input type="checkbox" checked={checked} onChange={() => onToggle(key)} />
                </td>
                <td>{serie.shipmentId}</td>
                <td>{formatDeviceIdentifier(serie.shipmentId, serie.deviceId)}</td>
                <td>{serie.position ?? "—"}</td>
                {showCityColumn ? <td>{serie.destinationCity ?? "—"}</td> : null}
                <td>{serie.destinationCountry ?? "—"}</td>
                {showBoxesColumn ? <td>{serie.boxIdentifier ?? "—"}</td> : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {isLoading ? <p className="analysis-table-hint">Chargement de la page…</p> : null}
    </div>
  );
}

function formatDeviceIdentifier(shipmentId: string, rawDeviceId: string | null | undefined): string {
  const normalized = (rawDeviceId ?? "").trim();
  if (!normalized) return "—";
  const prefix = `${shipmentId}-`;
  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length) || normalized;
  }
  return normalized;
}
